import { Locale } from '@/constants/locales';

export type TicketHolderInfo = {
    email: string;
    firstName: string;
    lastName: string;
    dateOfBirth: Date | null;
    streetName: string;
    streetNumber: string;
    city: string;
    country: string;
    zip: string;
    photo?: boolean;
};

export type TicketHolderDataProperty = keyof TicketHolderInfo;
export type TicketHolderFormField = {
    name: TicketHolderDataProperty;
    required: boolean;
};
export type TicketHolderFormSchemaItem = TicketHolderFormField;
export type TicketHolder = {
    id: string;
    ticketId: string;
    data: TicketHolderInfo;
};

export type Photo = {
    photoId: string;
};
export const ORDER_ITEM_TYPE = {
    TICKET: 'TICKET',
    SEASON_PASS: 'SEASON_PASS',
    SEASON_PASS_RENEWAL: 'SEASON_PASS_RENEWAL',
    VOUCHER_PURCHASE: 'VOUCHER_PURCHASE',
    FLEXIBLE_TICKET_PURCHASE: 'FLEXIBLE_TICKET_PURCHASE',
    OPEN_DATE_TICKET_PURCHASE: 'OPEN_DATE_TICKET_PURCHASE',
} as const;
export type OrderItemType =
    (typeof ORDER_ITEM_TYPE)[keyof typeof ORDER_ITEM_TYPE];

export const FLEX_TICKET_STATUS = {
    ACTIVE: 'ACTIVE',
    CANCELLED: 'CANCELLED',
} as const;

export const TICKET_STATUS = {
    PENDING_TICKET_HOLDER_INFO: 'PENDING_TICKET_HOLDER_INFO',
    ACTIVE: 'ACTIVE',
    CANCELLED: 'CANCELLED',
} as const;

export type FlexTicketStatusType =
    (typeof FLEX_TICKET_STATUS)[keyof typeof FLEX_TICKET_STATUS];
export type OrderItemTicketStatus =
    (typeof TICKET_STATUS)[keyof typeof TICKET_STATUS];
export type OrderItemTicket = {
    code: string;
    id: string;
    status: OrderItemTicketStatus;
};
export type OrderItem = {
    type: OrderItemType;
    id: string;
    name: string;
    status: FlexTicketStatusType;
    tickets: OrderItemTicket[];
    restrictions: {
        ticketHolderInfo: TicketHolderFormField[];
    };
    cancellationOptions: CancellationOption[];
};

export type CancellationOption = {
    id: string;
    type: CancellationOptionType;
    restrictions: CancellationOptionRestrictions;
    refundOptions: RefundOption[];
};

// Per FEE-2218 each cancellation option carries one or more refund options.
// MVP only needs the id (we hardcode refund_method=ORIGINAL_METHOD upstream and
// trust the policy offers it); fee + method shapes are intentionally elided
// until the picker UI is introduced post-MVP.
export type RefundOption = {
    id: string;
};

export const CANCELLATION_OPTION_TYPE = {
    CANCEL: 'CANCEL',
    WITHDRAW: 'WITHDRAW',
} as const;
export type CancellationOptionType =
    (typeof CANCELLATION_OPTION_TYPE)[keyof typeof CANCELLATION_OPTION_TYPE];
export type CancellationOptionRestrictions = {
    validityPeriod: FixedDateRange;
};

export type FixedDateRange = {
    startAt: Date;
    endAt: Date;
};

export type OrderDetails = {
    id: string;
    code: string;
    locale: Locale;
    orderItems: OrderItem[];
};

// The withdrawal flow works with full orders (listSelfServiceOrders returns full order objects).
export type Order = OrderDetails;

export type Money = {
    value: number;
    currency: string;
};

// Wire shape submitted to POST /orders/{order}/changes (FEE-2213). One entry
// per cancellable order item. `refund_method` is hardcoded to ORIGINAL_METHOD
// in MVP; expanded to a customer pick post-MVP.
export const REFUND_METHOD = {
    ORIGINAL_METHOD: 'ORIGINAL_METHOD',
    VOUCHER: 'VOUCHER',
    REQUEST: 'REQUEST',
} as const;
export type RefundMethod = (typeof REFUND_METHOD)[keyof typeof REFUND_METHOD];

export type OrderChangeRequestItem = {
    type: 'CANCEL_REFUND';
    order_item_id: string;
    cancellation_option_id: string;
    refund_option_id: string;
    refund_method: RefundMethod;
};

export type RefundQueryResult = {
    refundableAmount: Money;
    paidAmount?: Money;
    explanation_codes: string[];
    // Per FEE-2210 the available-refund response distinguishes full vs partial
    // refunds explicitly. Optional because the backend may omit it on the
    // current MVP path; the success-page reducer falls back to comparing
    // refundable vs paid when this is undefined.
    fullRefund?: boolean;
};

export const ORDER_CHANGE_STATUS = {
    PENDING: 'PENDING',
    APPLIED: 'APPLIED',
    FAILED: 'FAILED',
} as const;
export type OrderChangeStatus =
    (typeof ORDER_CHANGE_STATUS)[keyof typeof ORDER_CHANGE_STATUS];

export type OrderChangeResult = {
    id: string;
    status: OrderChangeStatus;
};
